import { useEffect, useState } from 'react';
import { api } from '../api';
import { haptic } from '../telegram';
import type { CareDecisionDto, CareEventDto, PlantDto } from '../types';
import {
  Card,
  ErrorBanner,
  Loader,
  PlantAvatar,
  StatusBadge,
  daysAgo,
  formatDate,
} from '../components/common';

const QUICK_ACTIONS: { type: string; label: string }[] = [
  { type: 'watering', label: '💧 Полил' },
  { type: 'fertilizing', label: '🌾 Удобрил' },
  { type: 'pruning', label: '✂️ Обрезал' },
  { type: 'repotting', label: '🪴 Пересадил' },
];

const ESTABLISHMENT_LABELS: Record<string, string> = {
  newly_planted: 'недавно посажено',
  establishing: 'укореняется',
  established: 'прижилось',
  recently_repotted: 'недавно пересажено',
  recovery: 'восстанавливается',
  unknown: 'неизвестно',
};

const ENVIRONMENTS: { value: string; label: string; hint: string }[] = [
  { value: 'indoor', label: '🏠 дома', hint: 'полив по графику, дождь не учитываю' },
  { value: 'balcony', label: '🌤 на балконе', hint: 'учитываю погоду' },
  { value: 'outdoor', label: '🌳 сад / улица', hint: 'учитываю дождь и погоду' },
  { value: 'greenhouse', label: '🪴 теплица', hint: 'полив по графику' },
];

interface Props {
  plantId: string;
  onBack: () => void;
  onDiagnose: (plantId: string) => void;
}

export function PlantDetail({ plantId, onBack, onDiagnose }: Props) {
  const [plant, setPlant] = useState<PlantDto | null>(null);
  const [events, setEvents] = useState<CareEventDto[]>([]);
  const [decisions, setDecisions] = useState<CareDecisionDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showWhy, setShowWhy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  async function load() {
    try {
      const [p, e, d] = await Promise.all([
        api.plant(plantId),
        api.events(plantId),
        api.decisions(plantId).catch(() => []),
      ]);
      setPlant(p);
      setEvents(e);
      setDecisions(d);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить растение');
    }
  }

  useEffect(() => {
    void load();
  }, [plantId]);

  async function logEvent(type: string) {
    setBusy(true);
    try {
      await api.logEvent(plantId, type);
      haptic('success');
      await load();
    } catch {
      haptic('error');
    } finally {
      setBusy(false);
    }
  }

  async function uploadPhoto(file: File) {
    setBusy(true);
    try {
      await api.uploadPlantPhoto(plantId, file);
      haptic('success');
      await load();
    } catch {
      haptic('error');
    } finally {
      setBusy(false);
    }
  }

  async function remove(reason: 'given_away' | 'died' | 'other') {
    setBusy(true);
    try {
      await api.deletePlant(plantId, reason);
      haptic('success');
      onBack();
    } catch {
      haptic('error');
      setBusy(false);
    }
  }

  async function setEnvironment(value: string) {
    setBusy(true);
    try {
      await api.updatePlant(plantId, { environmentType: value });
      haptic('success');
      await load();
    } catch {
      haptic('error');
    } finally {
      setBusy(false);
    }
  }

  if (error) return <ErrorBanner message={error} />;
  if (!plant) return <Loader />;

  const environment = ENVIRONMENTS.find((item) => item.value === plant.environmentType);

  const lastDecision = decisions[0];

  return (
    <div>
      <button className="btn secondary" onClick={onBack} style={{ marginBottom: 12 }}>
        ← Назад
      </button>

      <Card>
        <div className="row">
          <label className="avatar-pick" title="Сменить фото">
            <PlantAvatar url={plant.photoUrl} name={plant.displayName} />
            <span className="avatar-badge">📷</span>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              disabled={busy}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void uploadPhoto(file);
                e.target.value = '';
              }}
            />
          </label>
          <div className="grow">
            <div className="row between">
              <span className="title">{plant.displayName}</span>
              <StatusBadge status={plant.status} />
            </div>
            <div className="hint">{plant.speciesText ?? 'вид не указан'}</div>
            {plant.scientificName ? <div className="hint"><i>{plant.scientificName}</i></div> : null}
          </div>
        </div>

        {plant.photoIsDefault ? (
          <div className="hint mt">
            Это картинка вида. Нажмите на неё, чтобы поставить фото своего растения.
          </div>
        ) : null}

        <div className="chips">
          {plant.locationText ? <span className="chip">{plant.locationText}</span> : null}

          <span className="chip">{ESTABLISHMENT_LABELS[plant.establishmentStatus] ?? plant.establishmentStatus}</span>
        </div>

        <div className="row mt" style={{ gap: 8, alignItems: 'center' }}>
          <span className="hint">Где растёт:</span>
          <select
            value={plant.environmentType ?? ''}
            disabled={busy}
            onChange={(e) => void setEnvironment(e.target.value)}
          >
            {!plant.environmentType ? <option value="">не указано</option> : null}
            {ENVIRONMENTS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
        <div className="hint">
          {environment ? environment.hint : 'не указано — считаю комнатным, дождь не учитываю'}
        </div>
        {plant.climateSummary ? <div className="mt">{plant.climateSummary}</div> : null}
        {plant.climate?.notes ? <div className="hint">{plant.climate.notes}</div> : null}

        <div className="hint mt">
          Последний полив: {daysAgo(plant.lastWateredAt)}
          {plant.plantedAt ? ` · посажено ${formatDate(plant.plantedAt)}` : ''}
          {plant.lastRepottedAt ? ` · пересажено ${formatDate(plant.lastRepottedAt)}` : ''}
        </div>
      </Card>

      {plant.nextTask ? (
        <Card>
          <div className="row between">
            <span className="title">Дальше: {plant.nextTask.title}</span>
            <span className="badge">{formatDate(plant.nextTask.scheduledAt, true)}</span>
          </div>
          {plant.nextTask.reasonSummary ? (
            <div className="hint mt">{plant.nextTask.reasonSummary}</div>
          ) : null}
          {lastDecision?.weatherFacts && lastDecision.weatherFacts.forecast_provider !== 'none' ? (
            <>
              <button className="btn secondary mt" onClick={() => setShowWhy((v) => !v)}>
                ❓ Почему этот день
              </button>
              {showWhy ? (
                <div className="hint mt">
                  Сценарий: {lastDecision.selectedScenario}. За {lastDecision.weatherFacts.window_hours} ч
                  ожидается {lastDecision.weatherFacts.precipitation_mm_total.toFixed(1)} мм осадков
                  (вероятность {Math.round(lastDecision.weatherFacts.precipitation_probability_max)}%)
                  {lastDecision.weatherFacts.temperature_max_c != null
                    ? `, максимум ${Math.round(lastDecision.weatherFacts.temperature_max_c)}°C`
                    : ''}
                  .
                </div>
              ) : null}
            </>
          ) : null}
        </Card>
      ) : null}

      <h2>Быстрые действия</h2>
      <div className="actions">
        {QUICK_ACTIONS.map((action) => (
          <button key={action.type} className="btn" disabled={busy} onClick={() => logEvent(action.type)}>
            {action.label}
          </button>
        ))}
        <button className="btn secondary" onClick={() => onDiagnose(plant.id)}>
          🔍 Есть проблема
        </button>
      </div>

      <h2>История</h2>
      {!events.length ? (
        <div className="hint">Пока пусто. Отмечайте действия — я буду учитывать их в плане ухода.</div>
      ) : (
        <ul className="timeline">
          {events.map((event) => (
            <li key={event.id}>
              <span className="date">{formatDate(event.performedAt)}</span>
              <span className="grow">
                {event.title}
                {event.performedByName ? <span className="hint"> · {event.performedByName}</span> : null}
              </span>
            </li>
          ))}
        </ul>
      )}
      <Card>
        {!confirmRemove ? (
          <button className="btn secondary danger" onClick={() => setConfirmRemove(true)}>
            🗑 Удалить из коллекции
          </button>
        ) : (
          <>
            <div className="title">Почему убираем «{plant.displayName}»?</div>
            <div className="hint mt">Напоминания прекратятся, история ухода сохранится.</div>
            <div className="actions">
              <button className="btn secondary" disabled={busy} onClick={() => void remove('given_away')}>
                🎁 Отдал(а)
              </button>
              <button className="btn secondary" disabled={busy} onClick={() => void remove('died')}>
                🥀 Погибло
              </button>
              <button className="btn secondary" disabled={busy} onClick={() => void remove('other')}>
                Другое
              </button>
              <button className="btn secondary" disabled={busy} onClick={() => setConfirmRemove(false)}>
                Отмена
              </button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
